import {
  DQMSortOrder,
  VolumeTimeRange,
  VolumeTimeRangeOverride,
} from '@fullstory/proto/fs/services/dx-objects';
import { PageInfoRequest, PageInfoResponse } from '@fullstory/proto/fs/shared/pagination';
import { useDebounce, useQueryParam } from '@fullstory/react-hooks';
import { AppDispatch, fullstorySelectors, RequestLifecycleAction } from '@fullstory/redux';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import { RootReducerState } from '@fullstory/redux/reducers';
import { useRequestState } from '@fullstory/redux/utils/hooks';
import { RequestState } from '@fullstory/redux-util';
import jstz from 'jstz';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_ORDER, PAGE_QUERY_PARAM, PAGE_SIZE } from '../utils';

// Model the request body for the Get{ObjectType}Settings API
interface Req<F> {
  orgId: string;
  filters: F[];
  pageInfo: PageInfoRequest | null;
  order: DQMSortOrder;
  query: string;
  timezone: string;
  timeRangeOverride: VolumeTimeRangeOverride | null;
  requiresFullIssues: boolean;
}

// TODO: Maybe we don't need so many generic types?
interface Params<T, F, R, J, K, L> {
  getDataAction: (body: Req<F>) => RequestLifecycleAction<J, K, L, R, T>;
  pageInfo: PageInfoResponse | null;
  selectRequestState: (state: RootReducerState) => RequestState<T>;
  archivedFilter: F;
  setArchivedFilter: React.Dispatch<React.SetStateAction<F>>;
  filters: F[];
  setFilters: React.Dispatch<React.SetStateAction<F[]>>;
  defaultArchivedFilter: F;
  pageSize: number;
  requiresFullIssues?: boolean;
}

// Generic data fetcher for DQM meant to work with multiple data types
export const useDQMData = <T, F, R, J, K, L>({
  getDataAction,
  pageInfo,
  selectRequestState,
  defaultArchivedFilter,
  archivedFilter,
  setArchivedFilter,
  filters,
  setFilters,
  pageSize = PAGE_SIZE,
  requiresFullIssues = false,
}: Params<T, F, R, J, K, L>) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const { isLoading, isReady } = useRequestState(selectRequestState);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState<DQMSortOrder>(DEFAULT_ORDER);
  const allFilters = useMemo(() => [archivedFilter, ...filters], [archivedFilter, filters]);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 250);
  const [dataFetchedAt, setDataFetchedAt] = useState<Date>();
  const showDataDirect = useSelector(fullstorySelectors.getIsDataDirect);

  const [page, setPage] = useQueryParam(PAGE_QUERY_PARAM);

  const fetch = useCallback(
    async (filters: F[], order: DQMSortOrder, query: string, pageInfo: PageInfoRequest | null) => {
      const timezone = jstz.determine().name();
      const timeRangeOverride = showDataDirect ? { volumeTimeRange: VolumeTimeRange.DAY_7 } : null;
      const request = getDataAction({
        orgId,
        order,
        pageInfo,
        filters,
        query,
        timezone,
        timeRangeOverride,
        requiresFullIssues,
      });
      try {
        await dispatch(request);
        setDataFetchedAt(new Date());
      } catch (e: any) {
        setError(e);
      }
    },
    [dispatch, orgId, getDataAction, showDataDirect, requiresFullIssues],
  );

  const refetch = useCallback(async () => {
    await fetch(allFilters, order, debouncedQuery, {
      pageSize,
      pageToken: '',
      skip: (parseInt(page || '1', 10) - 1) * pageSize,
    });
  }, [allFilters, debouncedQuery, fetch, order, page]);

  const totalRecords = useMemo(() => pageInfo?.totalRecords || 0, [pageInfo]);

  const resetAll = useCallback(() => {
    setArchivedFilter(defaultArchivedFilter);
    setFilters([]);
    setOrder(DEFAULT_ORDER);
    setSearchQuery('');
  }, [setArchivedFilter, defaultArchivedFilter, setFilters]);

  useEffect(() => {
    fetch(allFilters, order, debouncedQuery, {
      pageSize,
      pageToken: '',
      skip: (parseInt(page || '1', 10) - 1) * pageSize,
    });
  }, [debouncedQuery, fetch, page, allFilters, order]);

  return {
    dataFetchedAt,
    error,
    isLoading,
    isReady,
    totalRecords,
    order,
    setOrder,
    refetch,
    searchQuery,
    setSearchQuery,
    resetAll,
    page: parseInt(page || '1', 10),
    setPage,
  };
};
